import type {
  AiSuggestionDetail,
  AiSuggestionsResult,
  ContentRiskLevel,
  AiSuggestionKbMatch
} from "../types/aiSuggestion";
import { apiRequest } from "./apiClient";

const asRecord = (v: unknown): Record<string, unknown> | null => {
  if (v !== null && typeof v === "object" && !Array.isArray(v)) {
    return v as Record<string, unknown>;
  }
  return null;
};

const readStringOrNull = (v: unknown): string | null => {
  if (v == null) {
    return null;
  }
  if (typeof v === "string") {
    return v;
  }
  if (typeof v === "number" || typeof v === "boolean") {
    return String(v);
  }
  return null;
};

const readNumberOrNull = (v: unknown): number | null => {
  if (v == null) {
    return null;
  }
  if (typeof v === "number" && !Number.isNaN(v)) {
    return v;
  }
  if (typeof v === "string" && v.trim() !== "") {
    const n = Number(v);
    return Number.isNaN(n) ? null : n;
  }
  return null;
};

const readKbMatch = (v: unknown): AiSuggestionKbMatch | null => {
  const r = asRecord(v);
  if (!r) {
    return null;
  }
  return {
    found: typeof r.found === "boolean" ? r.found : undefined,
    confidence: readNumberOrNull(r.confidence) ?? undefined,
    matched_section_name: readStringOrNull(r.matched_section_name) ?? undefined
  };
};

const mapSuggestionRow = (raw: unknown, bucketConfidence: number | null): AiSuggestionDetail => {
  const r = asRecord(raw) ?? {};
  const itemConfidence = readNumberOrNull(r.confidence);
  return {
    suggestion_item_id: readStringOrNull(r.suggestion_item_id) ?? "",
    source: readStringOrNull(r.source),
    original_text: readStringOrNull(r.original_text),
    client_suggested_text: readStringOrNull(r.client_suggested_text),
    ai_suggested_text: readStringOrNull(r.ai_suggested_text),
    content_risk_level: readStringOrNull(r.content_risk_level),
    section_name: readStringOrNull(r.section_name),
    section_index: readNumberOrNull(r.section_index),
    content_index: readNumberOrNull(r.content_index),
    suggestion_type: readStringOrNull(r.type ?? r.suggestion_type),
    reason: readStringOrNull(r.reason),
    review_status: readStringOrNull(r.review_status),
    approved_text: readStringOrNull(r.approved_text),
    reviewed_at: readStringOrNull(r.reviewed_at),
    reviewed_by: readStringOrNull(r.reviewed_by),
    confidence: itemConfidence ?? bucketConfidence,
    kb_match: readKbMatch(r.kb_match)
  };
};

const readSuggestionsArray = (data: Record<string, unknown>): readonly unknown[] => {
  const raw = data.suggestions_json ?? data.suggestionsJson;
  if (Array.isArray(raw)) {
    return raw;
  }
  return [];
};

const readCount = (data: Record<string, unknown>, ...keys: string[]): number => {
  for (const k of keys) {
    const n = data[k];
    if (typeof n === "number" && !Number.isNaN(n)) {
      return n;
    }
    if (typeof n === "string" && n.trim() !== "") {
      const parsed = Number(n);
      if (!Number.isNaN(parsed)) {
        return parsed;
      }
    }
  }
  return 0;
};

/**
 * 
 * GET /generated-contracts/{id}/ai-suggestions/by-risk-level?content_risk_level=…
 * Returns a new object graph — does not mutate the raw API payload.
 */
export const fetchAiSuggestionsByRiskLevel = async (
  generatedContractId: string,
  contentRiskLevel: ContentRiskLevel
): Promise<AiSuggestionsResult> => {
  const path =
    `/generated-contracts/${encodeURIComponent(generatedContractId)}/ai-suggestions/by-risk-level` +
    `?content_risk_level=${encodeURIComponent(contentRiskLevel)}`;

  const response = await apiRequest(
    path,
    { method: "GET", cache: "no-store" },
    { requiresAuth: true, redirectOnUnauthorized: true }
  );

  const json: unknown = await response.json();
  const root = asRecord(json);
  const dataRaw = root?.data ?? json;
  const data = asRecord(dataRaw) ?? {};

  const bucketConfidence = readNumberOrNull(data.confidence);

  const suggestions = readSuggestionsArray(data).map((row) =>
    mapSuggestionRow(row, bucketConfidence)
  );

  const counts = asRecord(data.counts) ?? {};
  const redCount = readCount(data, "red_count", "redCount") || readCount(counts, "red", "legal");
  const amberCount = readCount(data, "amber_count", "amberCount") || readCount(counts, "amber", "business");
  const greenCount = readCount(data, "green_count", "greenCount") || readCount(counts, "green", "exec");

  const redTotal =
    readNumberOrNull(data.red_total) ??
    readNumberOrNull(data.redTotal) ??
    readNumberOrNull(counts.red_total) ??
    readNumberOrNull(counts.redTotal);
  const amberTotal =
    readNumberOrNull(data.amber_total) ??
    readNumberOrNull(data.amberTotal) ??
    readNumberOrNull(counts.amber_total) ??
    readNumberOrNull(counts.amberTotal);
  const greenTotal =
    readNumberOrNull(data.green_total) ??
    readNumberOrNull(data.greenTotal) ??
    readNumberOrNull(counts.green_total) ??
    readNumberOrNull(counts.greenTotal);

  return {
    suggestions,
    redCount,
    amberCount,
    greenCount,
    bucketConfidence,
    redTotal,
    amberTotal,
    greenTotal
  };
};

const RISK_LEVELS_TRY_ORDER: readonly ContentRiskLevel[] = [
  "LegalDecision",
  "BusinessDecision",
  "ExecDecision"
];

/**
 * Resolves a suggestion by scanning risk buckets (404 on empty bucket is ignored).
 */
export const fetchSuggestionByItemId = async (
  generatedContractId: string,
  suggestionItemId: string
): Promise<{ readonly suggestion: AiSuggestionDetail; readonly contentRiskLevel: ContentRiskLevel } | null> => {
  for (const level of RISK_LEVELS_TRY_ORDER) {
    try {
      const result = await fetchAiSuggestionsByRiskLevel(generatedContractId, level);
      const found = result.suggestions.find((s) => s.suggestion_item_id === suggestionItemId);
      if (found) {
        return { suggestion: found, contentRiskLevel: level };
      }
    } catch {
      /* empty bucket or network — try next */
    }
  }
  return null;
};
